import React from 'react';
import type { Dispatch, FC, SetStateAction } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ButtonItem } from '@atlaskit/menu';
import Spinner from '@atlaskit/spinner';
import { Box, Stack, xcss } from '@atlaskit/primitives';

import type { ManualRule } from '@atlassian/automation-manual-triggers';

import { useAutomationMenu } from '../common/ConfluenceManualRulesContainer';

type AutomationModalRuleProps = {
	rule: ManualRule;
	selectedRuleId: number | undefined;
	setSelectedRuleId: Dispatch<SetStateAction<number | undefined>>;
};

const ruleButtonStyle = xcss({
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderWidth: 'border.width',
	borderRadius: 'border.radius',
});

const selectedRuleButtonStyle = xcss({
	backgroundColor: 'color.background.accent.blue.subtlest',
	borderColor: 'color.border.accent.blue',
	borderStyle: 'solid',
	borderWidth: 'border.width.outline',
	borderRadius: 'border.radius',
});

const ruleNameStyle = xcss({
	// Using this to increase the height of the button itself.
	height: '28px',
});

export const AutomationModalRule: FC<AutomationModalRuleProps> = ({
	rule,
	selectedRuleId,
	setSelectedRuleId,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { analyticsSource, invokingRuleId } = useAutomationMenu();

	const isSelectedRule = selectedRuleId === rule.id;
	const isExecuting = rule.id === invokingRuleId;

	const ruleOnClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'selectManualTriggerAutomation',
				source: analyticsSource,
				attributes: {
					ruleId: rule.id.toString(),
				},
			},
		}).fire();
		setSelectedRuleId(rule.id);
	};

	return (
		<Box xcss={isSelectedRule ? selectedRuleButtonStyle : ruleButtonStyle}>
			<ButtonItem
				isDisabled={!!invokingRuleId}
				iconBefore={isExecuting ? <Spinner size="small" /> : null}
				onClick={ruleOnClick}
			>
				<Stack xcss={ruleNameStyle} alignBlock="center">
					{rule.name}
				</Stack>
			</ButtonItem>
		</Box>
	);
};
